import { InspectionModel, SendFailure } from '../../data/types';

/**
 * Pure core of the inspection push service: the per-externalId in-flight dedupe.
 * Kept dependency-free (no Dexie / API imports) so it is unit-testable in isolation —
 * the real push implementation is injected by inspectionPushService.ts.
 */

export interface PushCallbacks {
  /** Fired as soon as the local record is loaded (the hook mirrors it into React state). */
  onInspectionLoaded?: (inspection: InspectionModel) => void;
}

export interface PushOutcome {
  inspection: InspectionModel | null;
  /** Why the push failed, or null when it succeeded. */
  failure: SendFailure | null;
  /** 426 contract-version / 403 permission failures: retrying without intervention is pointless. */
  fatal: boolean;
}

export type PushImplementation = (
  externalId: string,
  callbacks?: PushCallbacks,
) => Promise<PushOutcome>;

/**
 * Serialises pushes per inspection: concurrent callers for the same externalId (the
 * submit screen and the global drain racing) share ONE running push instead of
 * double-posting. Different externalIds run independently.
 */
export class InspectionPushService {
  private inFlight = new Map<string, Promise<PushOutcome>>();

  constructor(private readonly impl: PushImplementation) {}

  isInFlight(externalId: string): boolean {
    return this.inFlight.has(externalId);
  }

  push(externalId: string, callbacks?: PushCallbacks): Promise<PushOutcome> {
    const existing = this.inFlight.get(externalId);
    if (existing) {
      return existing;
    }
    const promise = this.impl(externalId, callbacks).finally(() => {
      this.inFlight.delete(externalId);
    });
    this.inFlight.set(externalId, promise);
    return promise;
  }
}
